import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Upload, Save, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function buildImageSrc(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API}/files/${url}`;
}

export default function StatesAdminTab() {
  const { language } = useLanguage();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [drafts, setDrafts] = useState({}); // { state_key: 'url-or-path' }

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/states`);
      setStates(r.data.states || []);
    } catch { toast.error('Failed to load states'); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const setDraft = (key, val) => setDrafts(d => ({ ...d, [key]: val }));

  const onUploadFile = async (key, file) => {
    if (!file) return;
    setSavingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/upload`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      const path = up.data.path;
      await axios.put(`${API}/states/${key}/image`, { image_url: path }, { headers });
      toast.success(language === 'es' ? 'Imagen actualizada' : 'Image updated');
      setDrafts(d => { const n = { ...d }; delete n[key]; return n; });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setSavingKey(''); }
  };

  const onSaveUrl = async (key) => {
    const url = (drafts[key] || '').trim();
    setSavingKey(key);
    try {
      await axios.put(`${API}/states/${key}/image`, { image_url: url }, { headers });
      toast.success(language === 'es' ? 'Imagen actualizada' : 'Image updated');
      setDrafts(d => { const n = { ...d }; delete n[key]; return n; });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSavingKey(''); }
  };

  const onClearImage = async (key) => {
    setSavingKey(key);
    try {
      await axios.put(`${API}/states/${key}/image`, { image_url: '' }, { headers });
      toast.success(language === 'es' ? 'Imagen eliminada' : 'Image cleared');
      refresh();
    } catch { toast.error('Failed'); }
    finally { setSavingKey(''); }
  };

  if (loading) return <div className="text-sm text-slate-400 py-12 text-center" data-testid="states-admin-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="states-admin-tab">
      <div className="bg-gradient-to-r from-[#0B192C] to-[#142944] text-white rounded-xl px-6 py-4 flex items-center gap-3">
        <MapPin className="w-5 h-5 text-[#D4AF37]" />
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-base">{language === 'es' ? 'Imágenes de Estados' : 'State Images'}</h3>
          <p className="text-xs text-white/70">{language === 'es' ? 'Sube imágenes para los 24 estados. Los estados sin imagen muestran un placeholder dorado/azul.' : 'Upload hero images for the 24 states. States without an image show a navy/gold gradient placeholder.'}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {states.map(s => {
          const draft = drafts[s.key];
          const previewSrc = draft ? buildImageSrc(draft) : buildImageSrc(s.image_url);
          const isSaving = savingKey === s.key;
          return (
            <Card key={s.key} data-testid={`states-admin-card-${s.key}`}>
              <CardContent className="p-4 space-y-3">
                <div className="relative aspect-[4/3] bg-gradient-to-br from-[#0B192C] via-[#142944] to-[#0B192C] rounded-lg overflow-hidden">
                  {previewSrc ? (
                    <img src={previewSrc} alt={s.name} className="w-full h-full object-cover" />
                  ) : (
                    <>
                      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.18),transparent_60%)]" />
                      <div className="absolute inset-0 flex items-center justify-center font-heading text-white/15 text-6xl font-bold select-none">{s.name?.[0] || '?'}</div>
                    </>
                  )}
                  <div className="absolute top-2 left-2 bg-black/60 backdrop-blur text-white text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 flex items-center gap-1">
                    <MapPin className="w-2.5 h-2.5 text-[#D4AF37]" />{s.listing_count} {language === 'es' ? 'props' : 'listings'}
                  </div>
                  {s.image_url && !draft && (
                    <button type="button" onClick={() => onClearImage(s.key)} disabled={isSaving}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 hover:bg-red-500 text-white flex items-center justify-center transition-colors"
                      data-testid={`states-admin-clear-${s.key}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-slate-900">{s.name}</h4>
                  <p className="text-[11px] text-slate-400">{s.city_count} {language === 'es' ? 'ciudades' : 'cities'}</p>
                </div>
                <Input
                  placeholder={language === 'es' ? 'Pega URL o sube archivo…' : 'Paste image URL or upload…'}
                  value={draft ?? s.image_url ?? ''}
                  onChange={e => setDraft(s.key, e.target.value)}
                  className="h-9 text-xs bg-slate-50"
                  data-testid={`states-admin-url-${s.key}`}
                />
                <div className="flex gap-2">
                  <label className="flex-1 h-9 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer flex items-center justify-center gap-1.5 text-xs font-medium text-slate-600" data-testid={`states-admin-upload-${s.key}`}>
                    <Upload className="w-3.5 h-3.5" />{language === 'es' ? 'Subir' : 'Upload'}
                    <input type="file" accept="image/*" disabled={isSaving} onChange={e => onUploadFile(s.key, e.target.files[0])} className="hidden" />
                  </label>
                  <Button type="button" disabled={isSaving || drafts[s.key] === undefined}
                    onClick={() => onSaveUrl(s.key)}
                    className="h-9 px-3 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] text-xs font-semibold"
                    data-testid={`states-admin-save-${s.key}`}>
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />{language === 'es' ? 'Guardar' : 'Save'}</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
